import type { ProposalDocumentStatus } from '../types/proposalPresentation';
/**
 * API client for Proposals/Estimates feature
 */

import { apiClient } from './api';
import { authService } from './auth';
import type {
  ProposalListItem,
  ProposalDetail,
  ProposalCreateInput,
  ProposalPage,
  ProposalTier,
  ProposalLineItem,
  ProposalOptionalUpgrade,
  ProposalInspectionPhoto,
  ManufacturerResource,
  PageBulkUpdate,
  ProposalAcceptanceInput,
  ProposalTemplate,
  ProposalTemplateInput,
  ProposalStatus,
  ProposalTypeChoice,
  PublicProposal,
  ManufacturerProduct,
  OrganizationProductPricing,
  OrganizationProductPricingInput,
  ManufacturerWithProducts,
  ProposalTierLabel,
  TierType,
} from '../types/proposals';

// Helper to build query string
const buildQueryString = (params: Record<string, string | number | boolean | undefined>) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      searchParams.append(key, String(value));
    }
  });
  const qs = searchParams.toString();
  return qs ? `?${qs}` : '';
};

export const proposalsApi = {
  // ==================
  // Proposals
  // ==================

  /**
   * List all proposals for the organization
   */
  async list(params?: { status?: string; opportunity?: number; page?: number; search?: string }) {
    const qs = buildQueryString(params || {});
    const response = await apiClient.get(`/api/proposals/${qs}`);
    return (response as any).data as { count: number; results: ProposalListItem[] };
  },

  /**
   * Organization-wide proposal counts per status (ignores the status filter)
   */
  async statusCounts(params?: { opportunity?: number }) {
    const qs = buildQueryString(params || {});
    const response = await apiClient.get(`/api/proposals/status-counts/${qs}`);
    return (response as any).data as Record<ProposalStatus, number> & { total: number };
  },

  /**
   * Get a single proposal by ID
   */
  async get(id: number) {
    const response = await apiClient.get(`/api/proposals/${id}/`);
    return (response as any).data as ProposalDetail;
  },

  /**
   * Create a new proposal
   */
  async create(data: ProposalCreateInput) {
    const response = await apiClient.post('/api/proposals/', data);
    return (response as any).data as ProposalDetail;
  },

  /**
   * Update a proposal
   */
  async update(id: number, data: Partial<ProposalDetail>) {
    const response = await apiClient.patch(`/api/proposals/${id}/`, data);
    return (response as any).data as ProposalDetail;
  },

  async renumber(id: number, proposalNumber: string, expectedNumber: string) {
    const response = await apiClient.post(`/api/proposals/${id}/renumber/`, {
      proposal_number: proposalNumber,
      expected_number: expectedNumber,
    });
    return response.data as { proposal: ProposalDetail; next_number: string };
  },

  /**
   * Delete a proposal
   */
  async delete(id: number) {
    await apiClient.delete(`/api/proposals/${id}/`);
  },

  /**
   * Send the proposal to the customer
   */
  async send(id: number, reviewVersion?: string, recipientEmail?: string) {
    const response = await apiClient.post(`/api/proposals/${id}/send/`, {
      review_version: reviewVersion,
      ...(recipientEmail !== undefined ? { recipient_email: recipientEmail } : {}),
    });
    return (response as any).data as { status: string; message: string; sent_at: string };
  },

  async markAsSent(id: number) {
    const response = await apiClient.post(`/api/proposals/${id}/mark-as-sent/`, {});
    return (response as any).data as { status: string; message: string; sent_at: string };
  },

  /**
   * Generate PDF for the proposal
   */
  async generatePdf(id: number) {
    const response = await apiClient.post(`/api/proposals/${id}/generate-pdf/`, {});
    return (response as any).data as ProposalDocumentStatus;
  },

  /**
   * Duplicate a proposal
   */
  async duplicate(id: number) {
    const response = await apiClient.post(`/api/proposals/${id}/duplicate/`, {});
    return (response as any).data as ProposalDetail;
  },

  // ==================
  // Pages
  // ==================

  /**
   * Get pages for a proposal
   */
  async getPages(proposalId: number) {
    const response = await apiClient.get(`/api/proposal-pages/?proposal=${proposalId}`);
    return (response as any).data as { count: number; results: ProposalPage[] };
  },

  /**
   * Update a page
   */
  async updatePage(pageId: number, data: Partial<ProposalPage>) {
    const response = await apiClient.patch(`/api/proposal-pages/${pageId}/`, data);
    return (response as any).data as ProposalPage;
  },

  /**
   * Bulk update pages (for reordering and toggling)
   */
  async bulkUpdatePages(pages: PageBulkUpdate[]) {
    const response = await apiClient.post('/api/proposal-pages/bulk-update/', { pages });
    return (response as any).data as { status: string };
  },

  /**
   * Create a new page (for custom pages or manufacturer resources)
   */
  async createPage(data: Partial<ProposalPage> & { proposal: number }) {
    const response = await apiClient.post('/api/proposal-pages/', data);
    return (response as any).data as ProposalPage;
  },

  /**
   * Delete a page
   */
  async deletePage(pageId: number) {
    await apiClient.delete(`/api/proposal-pages/${pageId}/`);
  },

  // ==================
  // Tiers
  // ==================

  /**
   * Get tiers for a proposal
   */
  async getTiers(proposalId: number) {
    const response = await apiClient.get(`/api/proposal-tiers/?proposal=${proposalId}`);
    return (response as any).data as { count: number; results: ProposalTier[] };
  },

  /**
   * Update a tier
   */
  async updateTier(tierId: number, data: Partial<ProposalTier>) {
    const response = await apiClient.patch(`/api/proposal-tiers/${tierId}/`, data);
    return (response as any).data as ProposalTier;
  },

  /**
   * Recalculate tier totals
   */
  async recalculateTier(tierId: number) {
    const response = await apiClient.post(`/api/proposal-tiers/${tierId}/recalculate/`, {});
    return (response as any).data as ProposalTier;
  },

  /**
   * Manually set a tier's product (from any manufacturer), or pass null to
   * clear the override and revert to the featured manufacturer's default.
   */
  async setTierProduct(tierId: number, productId: number | null) {
    const response = await apiClient.post(`/api/proposal-tiers/${tierId}/set-product/`, {
      product: productId,
    });
    return (response as any).data as ProposalTier;
  },

  // ==================
  // Line Items
  // ==================

  /**
   * Create a line item
   */
  async createLineItem(data: Partial<ProposalLineItem> & { tier: number }) {
    const response = await apiClient.post('/api/proposal-line-items/', data);
    return (response as any).data as ProposalLineItem;
  },

  /**
   * Update a line item
   */
  async updateLineItem(itemId: number, data: Partial<ProposalLineItem>) {
    const response = await apiClient.patch(`/api/proposal-line-items/${itemId}/`, data);
    return (response as any).data as ProposalLineItem;
  },

  /**
   * Delete a line item
   */
  async deleteLineItem(itemId: number) {
    await apiClient.delete(`/api/proposal-line-items/${itemId}/`);
  },

  // ==================
  // Optional Upgrades
  // ==================

  /**
   * Create an optional upgrade
   */
  async createUpgrade(data: Partial<ProposalOptionalUpgrade> & { proposal: number }) {
    const response = await apiClient.post('/api/proposal-upgrades/', data);
    return (response as any).data as ProposalOptionalUpgrade;
  },

  /**
   * Update an optional upgrade
   */
  async updateUpgrade(upgradeId: number, data: Partial<ProposalOptionalUpgrade>) {
    const response = await apiClient.patch(`/api/proposal-upgrades/${upgradeId}/`, data);
    return (response as any).data as ProposalOptionalUpgrade;
  },

  /**
   * Delete an optional upgrade
   */
  async deleteUpgrade(upgradeId: number) {
    await apiClient.delete(`/api/proposal-upgrades/${upgradeId}/`);
  },

  // ==================
  // Inspection Photos
  // ==================

  /**
   * Add a photo to the proposal
   */
  async addPhoto(data: { proposal: number; photo: number; description?: string }) {
    const response = await apiClient.post('/api/proposal-photos/', data);
    return (response as any).data as ProposalInspectionPhoto;
  },

  /**
   * Bulk add photos to a proposal
   */
  async bulkAddPhotos(proposalId: number, photoIds: number[]) {
    const response = await apiClient.post('/api/proposal-photos/bulk-add/', {
      proposal_id: proposalId,
      photo_ids: photoIds,
    });
    return (response as any).data as {
      status: string;
      created_count: number;
      created_ids: number[];
    };
  },

  /**
   * Update a photo description
   */
  async updatePhoto(photoId: number, data: Partial<ProposalInspectionPhoto>) {
    const response = await apiClient.patch(`/api/proposal-photos/${photoId}/`, data);
    return (response as any).data as ProposalInspectionPhoto;
  },

  /**
   * Remove a photo from the proposal
   */
  async removePhoto(photoId: number) {
    await apiClient.delete(`/api/proposal-photos/${photoId}/`);
  },

  // ==================
  // Manufacturer Resources
  // ==================

  /**
   * List manufacturer resources
   */
  async listResources() {
    const response = await apiClient.get('/api/manufacturer-resources/');
    return (response as any).data as { count: number; results: ManufacturerResource[] };
  },

  /**
   * Get resources by manufacturer
   */
  async getResourcesByManufacturer(manufacturerId: number) {
    const response = await apiClient.get(`/api/manufacturer-resources/by-manufacturer/${manufacturerId}/`);
    return (response as any).data as ManufacturerResource[];
  },

  /**
   * Create a manufacturer resource
   */
  async createResource(data: FormData) {
    const response = await apiClient.post('/api/manufacturer-resources/', data);
    return (response as any).data as ManufacturerResource;
  },

  // ==================
  // Public API (no auth)
  // ==================

  /**
   * Get public proposal by token.
   *
   * `preview` is for staff looking at their own proposal — it goes through the
   * authenticated client so the backend can recognise the owning org and skip
   * stamping the proposal as viewed by the customer. A signed-out visitor on a
   * ?preview=1 link still gets the plain public view rather than a login redirect.
   */
  async getPublic(token: string, options?: { preview?: boolean }) {
    if (options?.preview && authService.isAuthenticated()) {
      const response = await apiClient.get(`/api/proposals/public/${token}/?preview=1`);
      return (response as any).data as PublicProposal;
    }
    const response = await fetch(`/api/proposals/public/${token}/`);
    if (!response.ok) {
      throw new Error('Proposal not found');
    }
    return response.json() as Promise<PublicProposal>;
  },

  /**
   * Accept a proposal
   */
  async accept(token: string, data: ProposalAcceptanceInput) {
    const response = await fetch(`/api/proposals/public/${token}/accept/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      const message = Object.values(error).flat().filter(value => typeof value === 'string').join(' ');
      throw Object.assign(new Error(message || 'Failed to accept proposal'), { responseData: error });
    }
    return response.json() as Promise<{
      status: string;
      message: string;
      accepted_tier: string;
      acceptance_date: string;
    }>;
  },

  // ==================
  // Proposal Templates
  // ==================

  /**
   * Get available proposal type choices
   */
  async getProposalTypes() {
    const response = await apiClient.get('/api/proposal-templates/types/');
    return (response as any).data as ProposalTypeChoice[];
  },

  /**
   * List all templates for the organization
   */
  async listTemplates() {
    const response = await apiClient.get('/api/proposal-templates/');
    return (response as any).data as { count: number; results: ProposalTemplate[] };
  },

  /**
   * Get a single template by ID
   */
  async getTemplate(id: number) {
    const response = await apiClient.get(`/api/proposal-templates/${id}/`);
    return (response as any).data as ProposalTemplate;
  },

  /**
   * Get template by proposal type
   */
  async getTemplateByType(proposalType: string) {
    const response = await apiClient.get(`/api/proposal-templates/by-type/${proposalType}/`);
    return (response as any).data as ProposalTemplate | null;
  },

  /**
   * Create a new template
   */
  async createTemplate(data: ProposalTemplateInput) {
    const response = await apiClient.post('/api/proposal-templates/', data);
    return (response as any).data as ProposalTemplate;
  },

  /**
   * Update a template
   */
  async updateTemplate(id: number, data: Partial<ProposalTemplateInput>) {
    const response = await apiClient.patch(`/api/proposal-templates/${id}/`, data);
    return (response as any).data as ProposalTemplate;
  },

  /**
   * Delete a template
   */
  async deleteTemplate(id: number) {
    await apiClient.delete(`/api/proposal-templates/${id}/`);
  },

  /**
   * Load default templates with pre-configured line items
   */
  async loadDefaultTemplates() {
    const response = await apiClient.post('/api/proposal-templates/load-defaults/', {});
    return (response as any).data as {
      status: string;
      created: number;
      templates: { id: number; proposal_type: string; name: string }[];
    };
  },

  // ==================
  // Manufacturer Products
  // ==================

  /**
   * List all active manufacturer products
   */
  async listManufacturerProducts(params?: {
    manufacturer?: number;
    is_residential?: boolean;
    is_commercial?: boolean;
    is_other?: boolean;
  }) {
    const qs = buildQueryString(params || {});
    const response = await apiClient.get(`/api/manufacturer-products/${qs}`);
    // API returns array directly (no pagination)
    const data = (response as any).data;
    return Array.isArray(data) ? data : ((data.results || []) as ManufacturerProduct[]);
  },

  // ==================
  // Organization Product Pricing
  // ==================

  /**
   * List organization's product pricing
   */
  async listProductPricing(params?: {
    product?: number;
    product__manufacturer?: number;
    tier_type?: string;
  }) {
    const qs = buildQueryString(params || {});
    const response = await apiClient.get(`/api/organization-product-pricing/${qs}`);
    const data = (response as any).data;
    return Array.isArray(data) ? data : ((data.results || []) as OrganizationProductPricing[]);
  },

  /**
   * Get product pricing grouped by manufacturer
   * Returns all products with org's pricing for each
   */
  async getProductPricingByManufacturer() {
    const response = await apiClient.get('/api/organization-product-pricing/by-manufacturer/');
    return (response as any).data as ManufacturerWithProducts[];
  },

  /**
   * Create pricing for a product
   */
  async createProductPricing(data: OrganizationProductPricingInput) {
    const response = await apiClient.post('/api/organization-product-pricing/', data);
    return (response as any).data as OrganizationProductPricing;
  },

  /**
   * Update pricing for a product
   */
  async updateProductPricing(id: number, data: Partial<OrganizationProductPricingInput>) {
    const response = await apiClient.patch(`/api/organization-product-pricing/${id}/`, data);
    return (response as any).data as OrganizationProductPricing;
  },

  /**
   * Delete pricing for a product
   */
  async deleteProductPricing(id: number) {
    await apiClient.delete(`/api/organization-product-pricing/${id}/`);
  },

  /**
   * Bulk update product pricing
   */
  async bulkUpdateProductPricing(
    pricing: Array<{
      product: number;
      price_per_square: string;
      minimum_price_per_square?: string | null;
      tier_type?: string | null;
    }>
  ) {
    const response = await apiClient.post('/api/organization-product-pricing/bulk-update/', {
      pricing,
    });
    return (response as any).data as {
      status: string;
      created: number;
      updated: number;
      errors: Array<{ product: number; error: string }>;
    };
  },

  // ===========================================================================
  // Tier Names
  // ===========================================================================

  /**
   * List the org's names for the three tiers, in good -> better -> best order.
   * Always returns all three, defaulting to Good/Better/Best.
   */
  async listTierLabels() {
    const response = await apiClient.get('/api/proposal-tier-labels/');
    return (response as any).data as ProposalTierLabel[];
  },

  /**
   * Rename tiers. A name equal to the built-in default clears the override.
   */
  async updateTierLabels(labels: Array<{ tier_type: TierType; label: string }>) {
    const response = await apiClient.put('/api/proposal-tier-labels/bulk-update/', { labels });
    return (response as any).data as ProposalTierLabel[];
  },

  /**
   * Discard all custom tier names and go back to Good/Better/Best
   */
  async resetTierLabels() {
    const response = await apiClient.post('/api/proposal-tier-labels/reset/', {});
    return (response as any).data as ProposalTierLabel[];
  },
};
